import { useCallback, useEffect, useState } from "react";
import { settings as settingsApi, type Settings } from "../api/client";

/**
 * The user's settings, loaded once and updated in place.
 *
 * `undefined` while loading is distinct from `null` on failure — the home-point prompt must not
 * flash on every launch before the answer arrives, and must not appear at all if we simply
 * could not ask.
 */
export function useSettings() {
  const [value, setValue] = useState<Settings | null | undefined>(undefined);

  const reload = useCallback(async () => {
    try {
      setValue(await settingsApi.get());
    } catch {
      setValue(null);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const patch = useCallback(
    async (changes: Parameters<typeof settingsApi.patch>[0]) => {
      const updated = await settingsApi.patch(changes);
      setValue(updated);
      return updated;
    },
    [],
  );

  return { settings: value, patch, reload, setSettings: setValue };
}

/**
 * Post the device's position while the app is open, so 'auto' alert rules follow you (PLAN D4).
 *
 * Deliberately infrequent. This is the anchor for a 2 km zone, not a navigation trace — a fix
 * every couple of minutes is plenty, and anything more is battery spent for no accuracy that
 * matters at this scale.
 */
export function useLivePositionReporting(enabled: boolean, intervalMs = 120_000) {
  useEffect(() => {
    if (!enabled || !navigator.geolocation) return;

    let cancelled = false;
    const report = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          void settingsApi.livePosition(pos.coords.latitude, pos.coords.longitude).catch(() => undefined);
        },
        () => undefined,
        { enableHighAccuracy: false, timeout: 15_000, maximumAge: 60_000 },
      );
    };

    report();
    const id = setInterval(report, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, intervalMs]);
}
